import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowRight, Globe, BarChart3, Sparkles, Shield, Users,
  FileText, TrendingUp, Check, Star, ChevronRight,
  ShoppingBag, Layers, Zap, MousePointer, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage({ onLogin }) {
  const { t, language } = useLanguage();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [stats, setStats] = useState({ portals: 0, articles: 0, advertisers: 0, publishers: 0 });
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Pobierz dynamiczne statystyki i posty bloga
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, blogRes] = await Promise.all([
          fetch(`${API}/api/public/stats`),
          fetch(`${API}/api/public/blog?limit=3`)
        ]);
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }
        if (blogRes.ok) {
          const posts = await blogRes.json();
          setBlogPosts(posts);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Globe, titleKey: "feature1Title", descKey: "feature1Desc" },
    { icon: Sparkles, titleKey: "feature2Title", descKey: "feature2Desc" },
    { icon: BarChart3, titleKey: "feature3Title", descKey: "feature3Desc" },
    { icon: Shield, titleKey: "feature4Title", descKey: "feature4Desc" },
    { icon: Layers, titleKey: "feature5Title", descKey: "feature5Desc" },
    { icon: TrendingUp, titleKey: "feature6Title", descKey: "feature6Desc" },
  ];

  // Dynamiczne statystyki z API
  const displayStats = [
    { value: stats.portals > 0 ? `${stats.portals}+` : "---", labelKey: "statsPortals" },
    { value: stats.articles > 0 ? `${stats.articles.toLocaleString()}+` : "---", labelKey: "statsArticles" },
    { value: stats.advertisers > 0 ? `${stats.advertisers}+` : "---", labelKey: "statsAdvertisers" },
    { value: "4", labelKey: "statsModels" },
  ];

  const howItWorks = [
    { step: "1", titleKey: "how1Title", descKey: "how1Desc", icon: ShoppingBag },
    { step: "2", titleKey: "how2Title", descKey: "how2Desc", icon: Sparkles },
    { step: "3", titleKey: "how3Title", descKey: "how3Desc", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* NAVBAR */}
      <Header onLogin={onLogin} />

      {/* HERO */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-blue-50 text-blue-700 border-blue-200 mb-6 text-sm px-4 py-1">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t("landing.badge")}
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight tracking-tight mb-6">
            {t("landing.heroTitle1")}<br />
            <span className="text-blue-600">{t("landing.heroTitle2")}</span>
          </h1>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t("landing.heroDesc")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={onLogin} className="h-12 px-8 text-base gap-2 rounded-xl" data-testid="hero-cta-start">
              {t("landing.ctaStart")} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="lg" className="h-12 px-8 text-base rounded-xl" asChild data-testid="hero-cta-browse">
              <a href="/shop-public">{t("landing.ctaBrowse")}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {displayStats.map((s, i) => (
              <div key={i} className="text-center">
                <p className={`text-3xl sm:text-4xl font-bold text-slate-900 ${loadingStats ? 'animate-pulse' : ''}`}>{s.value}</p>
                <p className="text-sm text-slate-500 mt-1">{t(`landing.${s.labelKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{t("landing.featuresTitle")}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{t("landing.featuresDesc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="p-6 rounded-2xl border border-slate-100 hover:border-blue-100 hover:shadow-lg transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                  <f.icon className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t(`landing.${f.titleKey}`)}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{t(`landing.${f.descKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-16">{t("landing.howTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((s, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-6">
                  <s.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t(`landing.${s.titleKey}`)}</h3>
                <p className="text-sm text-slate-500">{t(`landing.${s.descKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG - posty z epidemos.pl */}
      {blogPosts.length > 0 && (
        <section id="blog" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{t("landing.blogTitle")}</h2>
              <p className="text-lg text-slate-500">{t("landing.blogDesc")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug || post.id}`}
                  className="group rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {post.featured_image && (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.published_at ? new Date(post.published_at).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US') : ''}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-3">{post.excerpt}</p>
                    <div className="mt-4 flex items-center text-sm text-blue-600 font-medium">
                      {t("landing.readMore")} <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t("landing.ctaTitle")}</h2>
          <p className="text-blue-100 text-lg mb-8">{t("landing.ctaDesc")}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 h-12 px-8 rounded-xl" onClick={onLogin} data-testid="cta-free-account">
              {t("landing.ctaFree")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-blue-700 h-12 px-8 rounded-xl" asChild>
              <a href="/shop-public">{t("landing.ctaShop")}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
